import { h, Component } from 'preact';
import cx from 'classnames';
import linkState from 'linkstate';
import { Subscription } from 'rxjs';

import { GRAY_PIXEL } from '../../../../constants/color-pixels';
import { Listing, RequestState, REQUEST_IN_PROGRESS, User } from '../../../../types';
import { Form } from '../../../utils/forms/form';
import { toDecimal } from '../../../../core/decimal';
import { hopplerApi, PHOTO_PARAMS } from '../../../../constants';
import * as roles from '../../../core/roles';
import { Role } from '../../../../types';

import '../../../utils/lazyload';
import { ListingUploadForField } from '../listings/listing-upload-for-field.component';
import { appState$ } from '../../store';
import { map, filter } from 'rxjs/operators';


export interface PropertyPageProps {
  listing?: Listing;
  form?: Form;
  onSave?: Function;
  onEdit?: Function;

  submitPropertyStatus?: RequestState;
}

export interface PropertyPageState {
  isAgreedTNC?: boolean;
  isAgreedCommissionAgreement?: boolean;
  additionalDescription?: string;
  isDescriptionOpen?: boolean;
}

export class PropertyPage extends Component<PropertyPageProps, PropertyPageState> {
  subscriptions: Subscription[] = [];
  currentUser: User;

  constructor(props) {
    super(props);

    appState$.pipe(
      map(store => store.user),
      filter(user => !!(user && user.currentUser)),
      map(user => user.currentUser)
    ).subscribe(user => this.currentUser = user);
  }

  getTitleString(listing: Listing) {
    const basicTitle = `${listing.type} for ${listing.category} in ${listing.area}, ${listing.city}`;
    if (listing.division === 'Residential' && listing.type !== 'Vacant Lot') {
      const bedrooms = listing.bedrooms > 0 ? `${listing.bedrooms}BR` : 'Studio';
      return `${bedrooms} ${basicTitle}`
    } else {
      return basicTitle;
    }
  }

  getCarousel(props: PropertyPageProps, state: PropertyPageState) {
    return (
      <div className="hi-listing-page__carousel-container" id="hiListingCarouselContainer">
        {/* PRESELLING FLAG HERE */}

        <div className="hi-listing-page__carousel hi-listing-page__carousel-property carousel slide hi-lazy"
          id="hiListingCarousel">

          {/* <div className="hi-listings-page__carousel-toggle">
            <button className="btn btn-outline-dark hi-listings-page__carousel-toggle-btn"
              data-hi-class-toggle="building" data-target='hiListingCarouselContainer'>
              View Building Photos
            </button>
          </div> */}
          
          <ol className="carousel-indicators">
            { props.listing.multimedia ? props.listing.multimedia.map( (photo, index) => 
              <li data-target="#hiListingCarousel" data-slide-to={ index }
                className={ index == 0 ? 'active' : '' }>
              </li>
            ) : null
          }
          </ol>

          <div className="carousel-inner">
            { props.listing.multimedia ? props.listing.multimedia.map( (photo, index) => 
              <div className={ 'carousel-item ' + (index == 0 ? 'active' : '') }>
                <img className="hi-listing-page__carousel-img"
                  data-src={ `${hopplerApi.images}/${photo.media}?${PHOTO_PARAMS}` }
                  src={ `${hopplerApi.images}/${photo.media}?${PHOTO_PARAMS}` }/>
              </div>
            ) :
            <div className='carousel-item active'>
              <img className="hi-listing-page__carousel-img"
                src={ `${hopplerApi.images}/noPhoto02.png?${PHOTO_PARAMS}` }/>
            </div>
          }
          </div>
          <a href="#hiListingCarousel" className="carousel-control-prev" 
            role='button' data-slide='prev'>
            <span className="carousel-control-prev-icon" aria-hidden="true">
            </span>
            <span className="sr-only">Previous</span>
          </a>
          <a href="#hiListingCarousel" className="carousel-control-next" 
            role='button' data-slide='next'>
            <span className="carousel-control-next-icon" aria-hidden="true">
            </span>
            <span className="sr-only">Next</span>
          </a>
        </div>
      </div>
    )
  }

  getSpinnerBlock() {
    return (
      <div className="mx-3">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }
  
  getActionsBlock(props: PropertyPageProps, state: PropertyPageState) {
    return (
      <div className="d-flex mt-3">
        <button className="btn btn-outline-dark" onClick={this.onEdit}>
          Edit
        </button>
        <button className="btn btn-secondary ml-3"
          disabled={
            !(state.isAgreedTNC && state.isAgreedCommissionAgreement) ||
            props.submitPropertyStatus == REQUEST_IN_PROGRESS
          }
          onClick={this.onSave}>
          Submit
        </button>
      </div>
    );
  }

  getResidentialDetails(props: PropertyPageProps, state: PropertyPageState) {
    let listing = props.listing;

    if (listing.division === 'Residential') {
      return (
        <div className="hi-listing-page__key-stats row no-gutters mb-0">
          <div className="col col-lg-6">
            <div className="hi-listing-page__key-stats__stat">
              { listing.bedrooms == 0 ? 'Studio' : listing.bedrooms + ' Bedrooms' }
            </div>
          </div>
          <div className="col col-lg-6">
            <div className="hi-listing-page__key-stats__stat">
              { listing.bathrooms > 1 ? listing.bathrooms + ' Bathrooms' : listing.bathrooms + ' Bathroom' }
            </div>
          </div>
        </div>
      )
    }

  }

  getRightSide(props: PropertyPageProps, state: PropertyPageState) {
    let listing = props.listing;

    return (
      <div className="hi-listing-page__right-section">
        <div className="hi-listing-page__right-section-property-info p-3">
          {
            listing.preSale ?
            <span className="hi-listing-page__starts-at">
              Starts at
            </span> : null
          }
          {
            listing.preSale ?
            <span className="hi-listing-page__price">
              Php { toDecimal(listing.salePrice || listing.rentalPrice) }
            </span> : null
          }
          {
            !listing.preSale ?
            <span className="hi-listing-page__price">
              Php { toDecimal(listing.salePrice || listing.rentalPrice || listing.pricePerSqm) }
              {
                listing.division === 'Commercial' ?
                <span className="hi-listing-page__price-per-sqm">
                  /sqm
                </span> : null
              }
            </span> : null
          }
          {
            !listing.preSale && listing.category === 'Rent' ?
            <span className="hi-listing-page__price-suffix">
              /month
            </span> : null
          }

          <div className="hi-listing-page__property-key mb-2 mt-3">
            <strong>
              { listing.propertyKey } (Property ID)
            </strong>
          </div>

          {
            this.getResidentialDetails(props, state)
          }

          <div className="hi-listing-page__key-stats row no-gutters">
            {
              listing.floorArea ? 
              <div className="col col-lg-6">
                <div className="hi-listing-page__key-stats__stat">
                  { listing.floorArea } sqm (floor area)
                </div>
              </div> : null
            }
            {
              listing.lotArea ? 
              <div className="col col-lg-6">
                <div className="hi-listing-page__key-stats__stat">
                  { listing.lotArea } sqm (lot area)
                </div>
              </div> : null
            }
          <div className="col col-lg-12">
            <div className="hi-listing-page__key-stats__stat">
              { listing.furnishing }
            </div>
          </div>
        </div>
        </div>
        
      </div>
    );

  }

  getUploadForField(props: PropertyPageProps, state: PropertyPageState) {
    const user = this.currentUser;
    let allowed: boolean;

    if (!user) {
      return;
    }

    allowed = roles.userHasAnyRole(user, [
      Role.SD_MANAGER,
      Role.SALES_DIRECTOR,
      Role.EXTERNAL_SALES_DIRECTOR
    ]);
    if (allowed) {
      return (
        <div className="my-2 d-flex align-items-start w-100 px-3">
          <ListingUploadForField onUserSelect={ (user) => this.onUploadForUserSelect(user) } />
        </div>
      );
    }
  }

  onEdit = () => {
    this.props.onEdit();
  }

  onSave = () => {
    this.props.onSave(this.state.additionalDescription);
  }

  onDescriptionToggle = () => {
    this.setState({
      isDescriptionOpen: !this.state.isDescriptionOpen
    })
  }

  onUploadForUserSelect(user: User) {
    const listing = this.props.listing;
    if (user) {
      listing.uploadFor = user.profile;
    } else {
      listing.uploadFor = null;
    }
  }

  render(props: PropertyPageProps, state: PropertyPageState) {
    let listing = props.listing;

    return (
      <div className="hi-kumita__property-preview">
        <div className="container-fluid">

          <div className="hi-kumita__property-preview__listing-container">
            <div className="hi-kumita__property-preview__listing-container__message">
              Almost There! Let's have a final look
            </div>

            <div className="hi-listing-page py-4">
              <div className="container-fluid">

                <div className="hi-listing-page__heading-row--desktop row">
                  <div className="col-12 col-lg-9">
                    <span className="hi-listing-page__h1 hi-listing-page__h1--top-heading">
                      { this.getTitleString(listing) }
                    </span>
                  </div>
                  <div className="col-12">
                    <span className="hi-type-caption">{ listing.area }, { listing.city }</span>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-8">
                    { this.getCarousel(props, state) }

                    <div className="hi-listing-page__main-details d-flex flex-column">
                      <div className="d-none d-md-flex row">
                        <div className="col-2">
                          <span className="hi-type-caps pt-0">About the Property</span>
                        </div>
                        <div className="col-9 offset-1">
                          <div
                            className={ cx('hi-listing-page__description', {'opened': state.isDescriptionOpen}) } 
                            id="hiDescriptionDesktop">
                            { listing.descriptionPrefill }
                          </div>
                          <button className="btn btn-link hi-listing-page__description-read-more"
                              onClick={this.onDescriptionToggle}>
                            Read more about the property 
                            <i className="fa fa-angle-down"></i>
                          </button>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-12 col-md-9 offset-md-3">
                          <label className="hi-input-label">Additional Description</label>
                          <textarea placeholder="Write your additional description here"
                            className="form-control hi-kumita__property-preview__additional-description"
                            value={ state.additionalDescription }
                            onInput={ linkState(this, 'additionalDescription') }
                            >
                          </textarea>
                        </div>
                      </div>

                      {
                        listing.amenities && listing.amenities.length > 0 ?
                        <div className="order-md-0 row mt-3">
                          <div className="col-md-2">
                            <span className="hi-type-caps pt-0">
                              Amenities
                            </span>
                          </div>
                          <div className="col-md-9 offset-md-1">
                            <div className="row">
                              {
                                listing.amenities.map((amenity, index) => {
                                  if (amenity.value) {
                                    if (amenity.field === 'Parking Spaces') {
                                      return (
                                        <div className="col-6">
                                          { amenity.value } { amenity.field }
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="col-6">
                                          { amenity.field }
                                        </div>
                                      );
                                    }
                                  }
                                })
                              }
                            </div>
                          </div>
                        </div> : null
                      }
                    </div>

                  </div>

                  <div className="col-lg-4">
                    { this.getRightSide(props, state) }
                  </div>

                </div>
              </div>
            </div>

            <div className="hi-kumita__property-preview__actions mt-4 py-4">
              { this.getUploadForField(props, state) }
              <label className="hi-type-caption my-2 d-flex align-items-start w-100 px-3">
                <input name="newsletter" type="checkbox" className="mr-2 mt-1"
                  checked={state.isAgreedTNC}
                  onChange={linkState(this, 'isAgreedTNC')}>
                </input>
                <span>
                  I have read and agreed to Hoppler's&nbsp; 
                  <a href="/terms-and-conditions" target="_blank">
                    Terms and Conditions
                  </a>
                  &nbsp;and&nbsp;
                  <a href="/privacy-policy" target="_blank">
                    Privacy Policy
                  </a>
                </span>
              </label>

              <label className="hi-type-caption my-2 d-flex align-items-start w-100 px-3">
                <input name="newsletter" type="checkbox" className="mr-2 mt-1"
                  checked={state.isAgreedCommissionAgreement}
                  onChange={linkState(this, 'isAgreedCommissionAgreement')}>
                </input>
                <span>
                  I have read and agreed to the
                  <a href="/terms-and-conditions#commissionagreement" target="_blank">
                    &nbsp;Commission Agreement&nbsp;
                  </a>
                  and will pay commission accordingly.
                </span>
              </label>

              {
                props.submitPropertyStatus == REQUEST_IN_PROGRESS ?
                this.getSpinnerBlock() :
                this.getActionsBlock(props, state)
              }
            </div>
          </div>
          
        </div>
      </div>
    );
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}
